// apps/client/src/components/layout/Sidebar.tsx
import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  // FileText,
  // Receipt,
  // CreditCard,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LogOut,
  Settings,
  X,
  User,
  HelpCircle,
  Users2,
  Truck,
  MapPinned,
} from "lucide-react";
import { useUIStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useClientPermissions } from "@/hooks/useClientPermissions";
import { Tooltip } from "@yogo/ui";

interface NavChild {
  label: string;
  href: string;
}
interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
  children?: NavChild[];
  color: string;
}

export default function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, sidebarMobileOpen, setMobileOpen } =
    useUIStore();
  const { user, logout } = useAuthStore();
  const { can } = useClientPermissions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    "/orders": true,
  });

  function toggleExpand(href: string) {
    setExpandedItems((p) => ({ ...p, [href]: !p[href] }));
  }

  // Tooltips are only useful when the sidebar is collapsed to icons-only —
  // wrapping with an empty-text Tooltip when expanded causes the shared
  // Tooltip component to still render its bubble, showing as a stray dot.
  function MaybeTooltip({
    show,
    text,
    children,
  }: {
    show: boolean;
    text: string;
    children: React.ReactNode;
  }) {
    if (!show) return <>{children}</>;
    return (
      <Tooltip text={text} position="right">
        {children}
      </Tooltip>
    );
  }

  async function handleLogout() {
    try {
      const accessToken = useAuthStore.getState().accessToken;
      await fetch(`${import.meta.env.VITE_API_URL}/api/v1/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch {
      // Even if API fails, clear local state
    } finally {
      logout();
      navigate("/login");
    }
  }

  // Built from the shared ACCESS_TABLE (useClientPermissions) rather than a
  // hardcoded role array here — see Day 87 notes: this is what prevents
  // Sidebar/router drifting out of sync, the recurring bug from the admin
  // RBAC build (Day 85/86).
  const navItems: NavItem[] = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      href: "/dashboard",
      color: "text-sky-500",
    },
    {
      label: "Orders",
      icon: Package,
      href: "/orders",
      color: "text-amber-500",
      children: [
        { label: "All Orders", href: "/orders" },
        { label: "New Order", href: "/orders/new" },
      ],
    },
    {
      label: "Live Fleet",
      icon: MapPinned,
      href: "/fleet",
      color: "text-green-400",
    },
    // {
    //   label: "Lorry Receipts",
    //   icon: FileText,
    //   href: "/lorry-receipts",
    //   color: "text-indigo-500",
    // },
    // ...(can("invoices")
    //   ? [
    //       {
    //         label: "Invoices",
    //         icon: Receipt,
    //         href: "/invoices",
    //         color: "text-fuchsia-500",
    //       },
    //     ]
    //   : []),
    // ...(can("payments")
    //   ? [
    //       {
    //         label: "Payments",
    //         icon: CreditCard,
    //         href: "/payments",
    //         color: "text-emerald-500",
    //       },
    //     ]
    //   : []),
    // Daily MIS menu removed: client receives the Daily MIS by email.
    ...(can("vehicleHireContracts")
      ? [
          {
            label: "Vehicle Hire",
            icon: Truck,
            href: "/vehicle-hire-contracts",
            color: "text-lime-500",
          },
        ]
      : []),
    ...(can("team")
      ? [
          {
            label: "Team",
            icon: Users2,
            href: "/team",
            color: "text-pink-500",
          },
        ]
      : []),
    {
      label: "Profile",
      icon: User,
      href: "/profile",
      color: "text-cyan-500",
    },
  ];

  const w = sidebarCollapsed ? "lg:w-[68px]" : "lg:w-[240px]";

  return (
    <>
      {/* Mobile overlay */}
      {sidebarMobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={[
          "fixed left-0 top-0 z-30 flex h-full flex-col",
          "bg-[#1B4FD8] text-white transition-all duration-300",
          w,
          sidebarMobileOpen
            ? "w-[240px] translate-x-0"
            : "w-[240px] -translate-x-full lg:translate-x-0",
        ].join(" ")}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-blue-600 px-4">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20">
                <span className="text-sm font-bold">Y</span>
              </div>
              <span className="text-base font-semibold tracking-tight">
                YOGO
              </span>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-white/20">
              <span className="text-sm font-bold">Y</span>
            </div>
          )}
          <Tooltip
            text={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            position="right"
          >
            <button
              onClick={
                sidebarMobileOpen ? () => setMobileOpen(false) : toggleSidebar
              }
              className="rounded-md p-1 text-blue-200 hover:bg-blue-700 hover:text-white transition lg:block"
              aria-label={
                sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
            >
              {sidebarMobileOpen ? (
                <X className="h-4 w-4" />
              ) : sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          </Tooltip>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4">
          <ul className="space-y-0.5 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const expanded = expandedItems[item.href];
              const isActive =
                item.href === "/dashboard"
                  ? pathname === item.href
                  : pathname.startsWith(item.href);

              return (
                <li key={item.href}>
                  {item.children ? (
                    // ── Item with children (accordion) ──────────────────────
                    <MaybeTooltip show={sidebarCollapsed} text={item.label}>
                      <button
                        onClick={() => {
                          if (sidebarCollapsed) {
                            toggleSidebar();
                            setExpandedItems((p) => ({
                              ...p,
                              [item.href]: true,
                            }));
                          } else toggleExpand(item.href);
                        }}
                        className={[
                          "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-blue-700 text-white"
                            : "text-blue-100 hover:bg-blue-700 hover:text-white",
                        ].join(" ")}
                      >
                        <Icon
                          className={`h-5 w-5 flex-shrink-0 ${item.color}`}
                        />
                        {!sidebarCollapsed && (
                          <>
                            <span className="flex-1 text-left">
                              {item.label}
                            </span>
                            {expanded ? (
                              <ChevronUp className="h-4 w-4 text-blue-300" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-blue-300" />
                            )}
                          </>
                        )}
                      </button>
                    </MaybeTooltip>
                  ) : (
                    // ── Regular nav item ────────────────────────────────────
                    <MaybeTooltip show={sidebarCollapsed} text={item.label}>
                      <NavLink
                        to={item.href}
                        end={item.href === "/dashboard"}
                        className={({ isActive: a }) =>
                          [
                            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                            a
                              ? "bg-blue-700 text-white"
                              : "text-blue-100 hover:bg-blue-700 hover:text-white",
                          ].join(" ")
                        }
                      >
                        <Icon
                          className={`h-5 w-5 flex-shrink-0 ${item.color}`}
                        />
                        {!sidebarCollapsed && (
                          <span className="flex-1">{item.label}</span>
                        )}
                      </NavLink>
                    </MaybeTooltip>
                  )}

                  {/* Children */}
                  {item.children && expanded && !sidebarCollapsed && (
                    <ul className="mt-0.5 space-y-0.5 pl-4">
                      {item.children.map((child) => (
                        <li key={child.href}>
                          <NavLink
                            to={child.href}
                            end
                            className={({ isActive: a }) =>
                              [
                                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                                a
                                  ? "bg-blue-600 text-white font-medium"
                                  : "text-blue-200 hover:bg-blue-700 hover:text-white",
                              ].join(" ")
                            }
                          >
                            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current opacity-60" />
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom */}
        <div className="border-t border-blue-600 p-3">
          {!sidebarCollapsed && user && (
            <div className="mb-2 flex items-center gap-3 px-2 py-2">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-semibold">
                {(user?.firstName || user?.name || "U").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {user?.firstName
                    ? `${user.firstName} ${user.lastName ?? ""}`.trim()
                    : user?.name}
                </p>
                <p className="truncate text-xs text-blue-300">{user?.email}</p>
                {user?.clientRole && user.clientRole !== "owner" && (
                  <p className="truncate text-2xs text-blue-200 mt-0.5">
                    {user.clientRole === "branch_manager"
                      ? "Branch Manager"
                      : "Staff"}
                    {" · "}
                    {user.branch ?? "Unassigned"}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1">
            {/* Settings → redirects to profile */}
            <MaybeTooltip show={sidebarCollapsed} text="Settings">
              <NavLink
                to="/profile"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-blue-200 hover:bg-blue-700 hover:text-white transition"
              >
                <Settings className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>Settings</span>}
              </NavLink>
            </MaybeTooltip>

            {/* Support → opens email */}
            <MaybeTooltip show={sidebarCollapsed} text="Support">
              <a
                href="mailto:support@yogosupplychainsolution.in"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-blue-200 hover:bg-blue-700 hover:text-white transition"
              >
                <HelpCircle className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>Support</span>}
              </a>
            </MaybeTooltip>

            {/* Logout */}
            <MaybeTooltip show={sidebarCollapsed} text="Log out">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-blue-200 hover:bg-red-700/50 hover:text-red-200 transition"
                aria-label="Log out"
              >
                <LogOut className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>Log out</span>}
              </button>
            </MaybeTooltip>
          </div>
        </div>
      </aside>
    </>
  );
}

// Day 68 complete
// Day 87: nav items filtered via useClientPermissions/ACCESS_TABLE instead
// of a static hardcoded array — prevents Sidebar/router drift
// Daily MIS: nav entry added, gated on can("mis") same pattern as
// invoices/payments/team
